import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import {
  CatCheckboxField,
  CatDropdown,
  CatHeading,
  CatList,
  CatListItem
} from 'components/blocks-components';
import { Conditional } from '@cat-ecom/pcc-components';
import { BlocksChangeEvent } from 'blocks-nextjs/blocks-web-components/events';
import {
  DEFAULT_LENGTH_VALUE,
  INVALID_TAX_FIELD_CHARS_REGEX,
  NAME_ERROR,
  taxModalInitialValue
} from '../constants';
import { TaxModalFormProps, FormValues } from '../types';
import { TaxModalFooter } from '../tax-modal-footer';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { TaxModalInput } from './tax-modal-input';
import { useCallback, useTransition } from 'react';
import { CompanyTaxFields } from './company-tax-fields/company-tax-fields';
import { onSubmitTaxModal } from 'services/tax-info/action';
import styles from '../tax-modal.module.scss';
import { getTaxIDValidation } from '../utils';

export const TaxModalForm = ({
  onClose,
  logoutUrl,
  taxInfo,
  espot
}: Readonly<TaxModalFormProps>) => {
  const t = useTranslations();
  const [isLoading, startTransition] = useTransition();

  const methods = useForm<FormValues>({
    mode: 'all',
    defaultValues: taxModalInitialValue
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    getValues,
    resetField,
    formState
  } = methods;
  const { isValid } = formState;

  const { individualLabel, isDealerEmailMarketingOptin, countryPayU } = taxInfo;

  const taxIdOptions = [
    {
      label: t('UR_CAT_COMPANY'),
      value: t('UR_CAT_COMPANY')
    },
    {
      label: t('UR_CAT_INDIVIDUAL'),
      value: t('UR_CAT_INDIVIDUAL')
    }
  ];

  const handleTaxIDValidation = useCallback(
    (value) => getTaxIDValidation(value, countryPayU, t),
    [countryPayU, t]
  );

  const selectedTaxIdOption = watch('taxIdOption');

  const setDropdownValue = (e) => {
    if (watch('taxIdOption') !== e.detail.value) {
      resetField('taxId');
      resetField('stateTaxId');
      resetField('neighborhood');
    }
    setValue('taxIdOption', e.detail.value);
  };

  const onSubmit = () => {
    const requestBody = {
      dealerFields: {
        ...getValues()
      }
    };
    startTransition(async () => {
      const { isSuccess } = await onSubmitTaxModal(requestBody);
      if (isSuccess) {
        onClose();
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
        <Controller
          name="taxIdOption"
          control={control}
          rules={{
            required: true
          }}
          render={({ field }) => (
            <CatDropdown
              {...field}
              name="taxIdOption"
              label={t('TAX_COLLECTION_DROPDOWN')}
              placeholder={t('TAX_COLLECTION_DROPDOWN_PLACEHOLDER')}
              required
              fieldId="taxIdOption"
              position="bottom-start"
              onBlSelect={(e: BlocksChangeEvent) => setDropdownValue(e)}
              value={getValues('taxIdOption')}
              className="mb-3"
            >
              <CatList>
                {taxIdOptions.map(({ label, value }) => (
                  <CatListItem value={value} key={value}>
                    {label}
                  </CatListItem>
                ))}
              </CatList>
            </CatDropdown>
          )}
        ></Controller>
        <Conditional test={!!getValues('taxIdOption')}>
          <Conditional
            test={selectedTaxIdOption === t('UR_CAT_INDIVIDUAL')}
            fallback={<CompanyTaxFields {...taxInfo} />}
          >
            <TaxModalInput
              label={individualLabel}
              name="taxId"
              type="text"
              required
              maxlength={DEFAULT_LENGTH_VALUE}
              data-testid="individualTaxId"
              className="mb-3"
              rules={{
                required: {
                  value: true,
                  message: t('ERROR_CAT_TAX_ID_INDIVIDUAL_REQUIRED')
                },
                pattern: {
                  value: INVALID_TAX_FIELD_CHARS_REGEX,
                  message: replaceTokensInString(
                    t('ADD_INV_CHAR_NEW_FIELD_WARNING', {
                      1: NAME_ERROR
                    })
                  )
                },
                validate: handleTaxIDValidation
              }}
            />
          </Conditional>
        </Conditional>
        <Conditional test={isDealerEmailMarketingOptin}>
          <CatCheckboxField
            name="dealerReceiveEmail"
            id="dealerReceiveEmail"
            {...register('sendDealerTaxEmailNotification')}
            className={styles['checkbox']}
          >
            <CatHeading variant="body-sm" className={styles['checkbox-label']}>
              {t('YES_SIGN_ME_UP')}
            </CatHeading>
          </CatCheckboxField>
          <CatHeading variant="footnote">
            <div className="mt-2 mb-3 d-flex flex-column row-gap-3">
              {espot}
            </div>
          </CatHeading>
        </Conditional>
        <TaxModalFooter
          logoutUrl={logoutUrl}
          isNotValid={!isValid || isLoading}
        />
      </form>
    </FormProvider>
  );
};
